import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { type Marker, MarkerClusterer } from "@googlemaps/markerclusterer"
import { useMap, InfoWindow } from "@vis.gl/react-google-maps"
import { Point } from "../Map"
import ShopMarker from "../ShopMarker"
import Schedule from "../../StoreList/Item/Schedule"

export default function MarkersLayer({ pointsList, setSelectedPoint, selectedPoint }) {
  const map = useMap()
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({})
  const isFirstRender = useRef(true)
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false)
  const [currentStoreAddress, setCurrentStoreAddress] = useState(null)
  const [storeSchedule, setStoreSchedule] = useState(null)
  const [currentStoreId, setCurrentStoreId] = useState(null)

  const clusterer = useMemo(() => {
    if (!map) return null

    return new MarkerClusterer({
      map,
      renderer: {
        render: ({ count, position }) => {
          const parser = new DOMParser()
          const svg = `<svg width="50" height="50" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle opacity=".2" cx="25" cy="25" r="25" fill="#00502F"/>
                  <circle cx="25" cy="25" r="19" fill="#0D6843"/>
                  <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="12" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
                  </svg>`
          const svgEl = parser.parseFromString(svg, "image/svg+xml").documentElement
          const clusterOptions: google.maps.marker.AdvancedMarkerElementOptions = {
            map,
            position,
            content: svgEl,
          }
          return new google.maps.marker.AdvancedMarkerElement(clusterOptions)
        },
      },
    })
  }, [map])

  // Update markers, if the markers array has changed
  useEffect(() => {
    if (!clusterer) return
    clusterer.clearMarkers()
    clusterer.addMarkers(Object.values(markers))
  }, [clusterer, markers, pointsList])

  // Center map on selected point
  useEffect(() => {
    if (selectedPoint && map) {
      if (selectedPoint !== currentStoreId) {
        setIsInfoWindowOpen(false)
      }
      const currentPoint = pointsList.find(({ id }) => id === selectedPoint) || pointsList[0]
      if (currentPoint) {
        const { lat, lng } = currentPoint
        map.panTo(new google.maps.LatLng(lat, lng))
      }
    }
  }, [selectedPoint, map, pointsList])

  // Center map on points list on first render (only if no selected point)
  useEffect(() => {
    if (map && pointsList && isFirstRender.current) {
      // If there's already a selected point, center on it instead of first point
      const targetPoint = selectedPoint 
        ? pointsList.find(({ id }) => id === selectedPoint) 
        : pointsList[0]
      
      if (targetPoint) {
        const { lat, lng } = targetPoint
        map.panTo(new google.maps.LatLng(lat, lng))
        map.setZoom(15)
        isFirstRender.current = false
      }
    }
  }, [pointsList, map, selectedPoint])

  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key])) return markers

      if (marker) {
        return { ...markers, [key]: marker }
      } else {
        const { [key]: _, ...newMarkers } = markers

        return newMarkers
      }
    })
  }, [])

  const markerClickHandler = useCallback((shop: Point) => {
    setSelectedPoint(shop.id)
    setCurrentStoreId(shop.id)
    setCurrentStoreAddress(shop.address)
    setStoreSchedule(new Schedule(shop.schedule))
    setIsInfoWindowOpen(true)
  }, [])

  useEffect(() => {
    if (map) {
      const closeInfoWindow = () => {
        isInfoWindowOpen && setIsInfoWindowOpen(false)
      }
      const clickListener = map.addListener("click", closeInfoWindow)
      const boundsChangedListener = map.addListener("zoom_changed", closeInfoWindow)
      const dragStartListener = map.addListener("dragstart", closeInfoWindow)

      return () => {
        google.maps.event.removeListener(clickListener)
        google.maps.event.removeListener(boundsChangedListener)
        google.maps.event.removeListener(dragStartListener)
      }
    }
  }, [map, isInfoWindowOpen])

  const statusLabel = useMemo(() => {
    if (!storeSchedule) return ""

    const { phrases } = window

    if (storeSchedule.isOpen) {
      return (
        phrases.shops.open_at.replace(":time", storeSchedule.todaySchedule[1]) ||
        `<span className="store__status--primary">Open</span> today until ${storeSchedule.todaySchedule[1]}`
      )
    }

    if (storeSchedule.nextWorkingDay === storeSchedule.todayName) {
      return (
        phrases.shops.closed_at.replace(":time", storeSchedule.nextWorkingDaySchedule[0]) ||
        `<span class="store__status--error">Closed</span> open at ${storeSchedule.nextWorkingDaySchedule[0]}`
      )
    }

    return (
      `${phrases.shops.closed_at_no_time} ${phrases?.shops?.days?.[storeSchedule.nextWorkingDay]} ${storeSchedule.nextWorkingDaySchedule[0]}` ||
      `<span class="store__status--error">Closed</span> open at`
    )
  }, [
    storeSchedule?.isOpen,
    storeSchedule?.todaySchedule,
    storeSchedule?.nextWorkingDay,
    storeSchedule?.todayName,
    storeSchedule?.nextWorkingDaySchedule,
  ])

  return (
    <>
      {pointsList?.map((point: Point) => (
        <ShopMarker
          key={point.id}
          shop={point}
          onClick={markerClickHandler}
          setMarkerRef={setMarkerRef}
        />
      ))}
      {isInfoWindowOpen && (
        <InfoWindow
          anchor={markers[selectedPoint]}
          headerDisabled={true}
          minWidth={250}
          maxWidth={300}
          className="info-window">
            <span className="address">{currentStoreAddress}</span>
            <span
              className="store-card__status"
              dangerouslySetInnerHTML={{ __html: statusLabel }}
            />
        </InfoWindow>
      )}
    </>
  )
}
